import { Box, Skeleton, Stack, Typography, styled } from '@mui/material';
import { TreeItem, SimpleTreeView } from '@mui/x-tree-view';
import type {
  DatabaseRecord,
  DBServerRecord,
  TableRecord,
} from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';

import { Virtuoso } from '@/components/shared/virtuoso';
import { useEnvironment } from '@/contexts/useEnvironment';

import { isNoSql } from './utils';

interface DBExplorerEntitiesTreeProps {
  objectTree: DBServerRecord | undefined;
  isLoading: boolean;
  type: 'db' | 'logs';
}

const DBTreeView = styled(SimpleTreeView)({});

const DBTreeItem = styled(TreeItem)({
  marginTop: '8px',
  marginBottom: '8px',
  '& i': {
    color: 'var(--mui-palette-custom-GREY-01)',
    fontSize: '21px',
    marginRight: '8px',
  },
  '& .MuiTreeItem-content': { height: '28px' },
});

export function DBExplorerEntitiesTree(props: DBExplorerEntitiesTreeProps) {
  const [expandedItems, setExpandedItems] = useState<string[] | undefined>(
    undefined
  );

  const handleExpandedItemsChange = (
    event: React.SyntheticEvent | null,
    itemIds: string[]
  ) => {
    setExpandedItems(itemIds);
  };

  const initialExpandedItems = useMemo(() => {
    if (props.objectTree) {
      const items: string[] = [];
      if (props.objectTree.databases?.length) {
        const db = props.objectTree.databases[0];
        items.push(
          `server-${props.objectTree?.serverId}-db-${db.name}&db==${db.name}`
        );
        items.push(
          `server-${props.objectTree?.serverId}-db-${db.name}-schemas&db==${db.name}`
        );
        if (db.schemas?.length) {
          const schema = db.schemas[0];
          items.push(
            `server-${props.objectTree?.serverId}-db-${db.name}-schema-${schema.name}&db==${db.name}`
          );
          items.push(
            `server-${props.objectTree?.serverId}-db-${db.name}-schema-${schema.name}-tables&db==${db.name}`
          );
        }
      }

      return items;
    }

    return ['loading_0', 'loading_0_0', 'loading_0_0_0'];
  }, [props.objectTree]);

  const isReady = props.objectTree && !props.isLoading;

  return (
    <>
      <div className='px-[24px] pt-[24px]' data-testid='db-entities-tree'>
        {props.isLoading && <Skeleton />}
        {!props.isLoading && props.type === 'logs' && (
          <Typography marginBottom='24px' variant='body1'>
            {props.objectTree?.databases?.length}{' '}
            {props.objectTree?.databases?.length || 0 > 1
              ? 'schemas on this data source'
              : 'schema on this data source'}
          </Typography>
        )}
        {!props.isLoading && props.type === 'db' && (
          <Typography marginBottom='24px' variant='body1'>
            {props.objectTree?.databases?.length}{' '}
            {props.objectTree?.databases?.length || 0 > 1
              ? 'databases on this snapshot'
              : 'database on this snapshot'}
          </Typography>
        )}
        <DBTreeView
          expandedItems={expandedItems || initialExpandedItems}
          onExpandedItemsChange={handleExpandedItemsChange}
        >
          {isReady && (
            <DatabaseNodes
              server={props.objectTree!}
              expandedItems={expandedItems || initialExpandedItems}
            />
          )}
          {!isReady && <LoadingStateNode dbs={2} schemas={1} tables={4} />}
        </DBTreeView>
      </div>
    </>
  );
}

export function LoadingStateNode({
  dbs,
  schemas,
  tables,
}: {
  dbs: number;
  schemas: number;
  tables: number;
}) {
  return [...Array(dbs)].map((_, index) => (
    <DBTreeItem
      key={`loading_${index}`}
      itemId={`loading_${index}`}
      label={<Skeleton width='250px' />}
    >
      {[...Array(schemas)].map((_, sindex) => (
        <DBTreeItem
          key={`loading_${index}_${sindex}`}
          itemId={`loading_${index}_${sindex}`}
          label={<Skeleton width='200px' />}
        >
          {[...Array(tables)].map((_, tindex) => (
            <DBTreeItem
              key={`loading_${index}_${sindex}_${tindex}`}
              itemId={`loading_${index}_${sindex}_${tindex}`}
              label={<Skeleton width='160px' />}
            />
          ))}
        </DBTreeItem>
      ))}
    </DBTreeItem>
  ));
}

function ColumnLabel({ label, type }: { label: string; type: string }) {
  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      className='w-full'
    >
      <Stack direction='row' alignItems='center'>
        <label title={label} className='truncate max-w-[200px]'>
          {label}
        </label>
      </Stack>
      <Typography variant='body2'>{type}</Typography>
    </Stack>
  );
}

function TableLabel({
  label,
  numOfRows,
  type,
}: {
  label: string;
  numOfRows: number;
  type: string;
}) {
  const isEstimated = type.toLowerCase() === 'dynamodb';

  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      className='w-full'
    >
      <Stack direction='row' alignItems='center'>
        <i className='material-symbols-table' />
        <span title={label} className='truncate max-w-[120px]'>
          {label}
        </span>
      </Stack>
      <Typography
        variant='body2'
        id='table-row-count'
        sx={{
          textAlign: 'end',
        }}
      >
        {isEstimated && '~ '}
        {numOfRows.toLocaleString()}
        {type === 'cosmos' ? ' items' : ' records'}
      </Typography>
    </Stack>
  );
}

function getCollectionName(type: string) {
  if (type === 'mongo') return 'Collections';
  if (type === 'cosmos') return 'Containers';
  return 'Tables';
}

function DBLabel({
  label,
  numOfTables,
  type,
}: {
  label: string;
  numOfTables: number;
  type: string;
}) {
  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      className='w-full'
    >
      <Stack direction='row' alignItems='center'>
        <i className='material-symbols-database-outline' />
        <Typography className='truncate max-w-[230px]' title={label}>
          {label}
        </Typography>
      </Stack>
      <Typography variant='body2'>
        {numOfTables.toLocaleString()} {getCollectionName(type)}
      </Typography>
    </Stack>
  );
}

const TableNode = ({
  table,
  virtualized,
  serverId,
  type,
  dbName,
  schemaName,
  expandedItems,
}: {
  serverId: string;
  dbName: string;
  schemaName: string;
  type: string;
  virtualized: boolean;
  table: TableRecord;
  expandedItems: string[];
}) => {
  const itemId = `server-${serverId}-db-${dbName}-schema-${schemaName}-table-${table.name}&db==${dbName}`;

  if (!table.columns || table.columns.length === 0) {
    return (
      <DBTreeItem
        sx={{ padding: 0 }}
        key={table.name}
        itemId={itemId}
        label={
          <TableLabel
            type={type}
            label={table.name || ''}
            numOfRows={table.rowCount || 0}
          />
        }
        slotProps={{
          groupTransition: {
            style: { ...(virtualized ? { transition: 'none' } : {}) },
          },
        }}
      />
    );
  }

  return (
    <DBTreeItem
      sx={{ padding: 0 }}
      key={table.name}
      itemId={itemId}
      label={
        <TableLabel
          type={type}
          label={table.name || ''}
          numOfRows={table.rowCount || 0}
        />
      }
      slotProps={{
        groupTransition: {
          style: { ...(virtualized ? { transition: 'none' } : {}) },
        },
      }}
    >
      {!expandedItems.includes(itemId) && <></>}
      {expandedItems.includes(itemId) &&
        table.columns.map((column) => (
          <DBTreeItem
            key={column.name}
            itemId={`server-${serverId}-db-${dbName}-schema-${schemaName}-table-${table.name}-column=${column.name}&db==${dbName}`}
            label={<ColumnLabel label={column.name} type={column.type} />}
          />
        ))}
    </DBTreeItem>
  );
};

function TablesNodes({
  tables,
  serverId,
  dbName,
  type,
  schemaName,
  expandedItems,
}: {
  tables: TableRecord[];
  serverId: string;
  schemaName: string;
  dbName: string;
  type: string;
  expandedItems: string[];
}) {
  if (tables.length < 100) {
    return (
      <Box>
        {tables.map((table) => {
          return (
            <TableNode
              serverId={serverId}
              dbName={dbName}
              virtualized={false}
              key={table.name}
              table={table}
              type={type}
              schemaName={schemaName}
              expandedItems={expandedItems}
            />
          );
        })}
      </Box>
    );
  }

  return (
    <Virtuoso
      data={tables}
      style={{ height: '400px' }}
      itemContent={(index, table) => (
        <TableNode
          type={type}
          serverId={serverId}
          dbName={dbName}
          virtualized={true}
          key={table.name}
          table={table}
          schemaName={schemaName}
          expandedItems={expandedItems}
        />
      )}
    />
  );
}

function Schemas({
  db,
  serverId,
  engineType,
  expandedItems,
}: {
  serverId: string;
  engineType: string;
  db: DatabaseRecord;
  expandedItems: string[];
}) {
  const itemId = `server-${serverId}-db-${db.name}-schemas&db==${db.name}`;

  if (!db.schemas || db.schemas.length === 0) {
    return <DBTreeItem itemId={itemId} label='Schemas' />;
  }

  return (
    <DBTreeItem itemId={itemId} label='Schemas'>
      {!expandedItems.includes(itemId) && <></>}
      {expandedItems.includes(itemId) &&
        db.schemas.map((schema) => {
          const itemId = `server-${serverId}-db-${db.name}-schema-${schema.name}&db==${db.name}`;
          const childItemId = `server-${serverId}-db-${db.name}-schema-${schema.name}-tables&db==${db.name}`;

          if (!schema.tables || schema.tables.length === 0) {
            return (
              <DBTreeItem key={schema.name} itemId={itemId} label={schema.name}>
                <DBTreeItem itemId={childItemId} label='Tables' />
              </DBTreeItem>
            );
          }

          return (
            <DBTreeItem key={schema.name} itemId={itemId} label={schema.name}>
              {!expandedItems.includes(itemId) && <></>}
              {expandedItems.includes(itemId) && (
                <DBTreeItem itemId={childItemId} label='Tables'>
                  {!expandedItems.includes(childItemId) && <></>}
                  {expandedItems.includes(childItemId) && (
                    <TablesNodes
                      serverId={serverId}
                      dbName={db.name}
                      type={engineType}
                      schemaName={schema.name}
                      tables={schema.tables}
                      expandedItems={expandedItems}
                    />
                  )}
                </DBTreeItem>
              )}
            </DBTreeItem>
          );
        })}
    </DBTreeItem>
  );
}

function Collections({
  db,
  serverId,
  type,
  expandedItems,
}: {
  db: DatabaseRecord;
  serverId: string;
  type: string;
  expandedItems: string[];
}) {
  const itemId = `server-${serverId}-db-${db.name}-schemas&db==${db.name}`;
  if (!db.schemas || db.schemas.length === 0) {
    return (
      <DBTreeItem
        itemId={itemId}
        label={type === 'cosmos' ? 'Containers' : 'Collections'}
      />
    );
  }

  return (
    <DBTreeItem
      itemId={itemId}
      label={type === 'cosmos' ? 'Containers' : 'Collections'}
    >
      {!expandedItems.includes(itemId) && <></>}
      {expandedItems.includes(itemId) &&
        db.schemas.map((schema) => (
          <TablesNodes
            serverId={serverId}
            dbName={db.name}
            schemaName={schema.name}
            key={schema.name}
            type={type}
            tables={schema.tables || []}
            expandedItems={expandedItems}
          />
        ))}
    </DBTreeItem>
  );
}

function DatabaseNodes({
  server,
  expandedItems,
}: {
  server: DBServerRecord;
  expandedItems: string[];
}) {
  const { isDemo, isDev } = useEnvironment();
  const isNoSqlServer = isNoSql(server.engineType);
  return server.databases?.map((db) => {
    const forceNoSqlServer =
      (isDemo || isDev) &&
      db.schemas?.length === 1 &&
      db.schemas[0].name === '';

    const itemId = `server-${server.serverId}-db-${db.name}&db==${db.name}`;
    return (
      <DBTreeItem
        key={db.name}
        itemId={itemId}
        label={
          <DBLabel
            label={db.name}
            type={server.engineType}
            numOfTables={
              db.schemas?.reduce(
                (acc, schema) => acc + (schema.tables?.length || 0),
                0
              ) ?? 0
            }
          />
        }
      >
        {!expandedItems.includes(itemId) && <></>}
        {expandedItems.includes(itemId) &&
          (isNoSqlServer || forceNoSqlServer) && (
            <Collections
              expandedItems={expandedItems}
              db={db}
              type={server.engineType}
              serverId={server.serverId}
            />
          )}
        {expandedItems.includes(itemId) &&
          !(isNoSqlServer || forceNoSqlServer) && (
            <Schemas
              expandedItems={expandedItems}
              db={db}
              engineType={server.engineType}
              serverId={server.serverId}
            />
          )}
      </DBTreeItem>
    );
  });
}
